import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";

// Maximum motivational snackbars visible at the same time.
const MAX_CONCURRENT = 2;
const AUTO_HIDE_MS = 5000;

// How long after suppressNextProfileBoost() an increase is still considered a
// side effect of that action. Expires on its own so a suppression that is never
// used (the action didn't actually raise completion) can't swallow a later one.
const SUPPRESS_WINDOW_MS = 10000;

let suppressUntil = 0;

// Call right before refreshing the candidate when the completion bump is
// incidental and the user is already getting feedback for what they did (e.g.
// applying to a job saves the uploaded CV on the profile).
export const suppressNextProfileBoost = () => {
  suppressUntil = Date.now() + SUPPRESS_WINDOW_MS;
};

// Pick an encouraging subtitle based on how far along the candidate is.
const encouragementId = (percentage) => {
  if (percentage >= 100) return "candidate.profile_boost.complete";
  if (percentage >= 70) return "candidate.profile_boost.almost_there";
  return "candidate.profile_boost.keep_going";
};

// Watches the candidate's profile completion and, each time it increases,
// shows a motivational snackbar with the new percentage. Never stacks more
// than MAX_CONCURRENT of these at once (the oldest is dismissed first).
const useProfileCompletionBoost = () => {
  const intl = useIntl();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const profileCompletion = useSelector(
    (state) => state.current_candidate?.profile_completion
  );

  const previousRef = useRef(null);
  const activeKeysRef = useRef([]);

  useEffect(() => {
    // Wait until the candidate is actually loaded, otherwise the first real
    // value would look like an increase from 0 and fire on initial load.
    if (profileCompletion === undefined || profileCompletion === null) return;

    const current = Number(profileCompletion) || 0;

    // Seed the baseline on the first known value without notifying.
    if (previousRef.current === null) {
      previousRef.current = current;
      return;
    }

    if (current > previousRef.current && Date.now() < suppressUntil) {
      suppressUntil = 0;
      previousRef.current = current;
      return;
    }

    if (current > previousRef.current) {
      const delta = current - previousRef.current;

      // Enforce the concurrency cap by closing the oldest one first.
      while (activeKeysRef.current.length >= MAX_CONCURRENT) {
        const oldest = activeKeysRef.current.shift();
        closeSnackbar(oldest);
      }

      const key = enqueueSnackbar("", {
        variant: "profileBoost",
        autoHideDuration: AUTO_HIDE_MS,
        percentage: current,
        delta,
        title: intl.formatMessage(
          { id: "candidate.profile_boost.title" },
          { percentage: current }
        ),
        subtitle: intl.formatMessage({ id: encouragementId(current) }),
        onExited: (_node, exitedKey) => {
          activeKeysRef.current = activeKeysRef.current.filter(
            (k) => k !== exitedKey
          );
        },
      });

      activeKeysRef.current.push(key);
    }

    previousRef.current = current;
  }, [profileCompletion, enqueueSnackbar, closeSnackbar, intl]);
};

export default useProfileCompletionBoost;
